import React from 'react';
import { Minus, Plus } from 'lucide-react';
import Tooltip from '../Tooltip';

const OptionsSupplementaires = ({
  optionsSupplementaires,
  optionsSupp,
  handleOptionChange,
  nombreSanglons,
  setNombreSanglons,
  isPergola,
  renfortLyreDetails,
  setRenfortLyreDetails,
  largeur,
  typeAssemblage,
  setTypeAssemblage,
  lestageDetails,
  setLestageDetails
}) => {
  return (
    <div>
      <h3 className="text-lg font-medium mb-3">Options supplémentaires</h3>
      
      <div className="grid grid-cols-3 gap-3">
        {optionsSupplementaires.map((option) => (
          <button
            key={option.id}
            onClick={() => handleOptionChange(option.id)}
            title={option.tooltip}
            className={`p-4 border-2 rounded-lg text-center transition-all ${
              option.id === 'sans' 
                ? (Object.values(optionsSupp).every(v => !v) ? 'border-celadon bg-celadon text-white' : 'border-gray-200 hover:border-gray-300')
                : (optionsSupp[option.id] ? 'border-celadon bg-celadon text-white' : 'border-gray-200 hover:border-gray-300')
            }`}
          >
            <p className="text-xs font-medium">{option.label}</p>
          </button>
        ))}
      </div>

      {/* Panneau dynamique pour Renfort de lyre */}
      {optionsSupp.renfortLyre && (
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-md font-medium mb-3 text-blue-900">Configuration du renfort de lyre</h4>
          
          {/* Nombre de renforts */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Nombre de renforts :</label>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  const newNombre = Math.max(1, renfortLyreDetails.nombre - 1);
                  setRenfortLyreDetails(prev => ({ ...prev, nombre: newNombre }));
                }}
                className="p-2 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
              >
                <Minus size={16} />
              </button>
              <input
                type="number"
                value={renfortLyreDetails.nombre}
                onChange={(e) => {
                  const value = Math.min(3, Math.max(1, Number(e.target.value)));
                  setRenfortLyreDetails(prev => ({ ...prev, nombre: value }));
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded text-center font-medium max-w-[100px]"
                min="1"
                max="3"
              />
              <button
                onClick={() => {
                  const newNombre = Math.min(3, renfortLyreDetails.nombre + 1);
                  setRenfortLyreDetails(prev => ({ ...prev, nombre: newNombre }));
                }}
                className="p-2 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
              >
                <Plus size={16} />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Entre 1 et 3 renforts maximum</p>
          </div>

          {/* Positions des renforts */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Position{renfortLyreDetails.nombre > 1 ? 's' : ''} (cm depuis le bord gauche) :
            </label>
            <div className="space-y-2">
              {renfortLyreDetails.positions.map((position, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="text-sm font-medium w-20">Renfort {index + 1}:</span>
                  <input
                    type="number"
                    value={Math.round(position)}
                    onChange={(e) => {
                      const newPositions = [...renfortLyreDetails.positions];
                      newPositions[index] = Number(e.target.value);
                      setRenfortLyreDetails(prev => ({ ...prev, positions: newPositions }));
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded text-center font-medium max-w-[120px]"
                    min="0"
                    max={largeur}
                  />
                  <span className="text-sm text-gray-500">cm</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
              <Tooltip tooltipKey="positions-calculees" size={14} />
              Positions calculées automatiquement selon la largeur ({largeur} cm)
            </p>
          </div>
        </div>
      )}

      {/* Panneau dynamique pour Type d'assemblage */}
      {optionsSupp.clips && (
        <div className="mt-4 bg-celadon-light/20 border border-celadon-light rounded-lg p-4">
          <h4 className="text-md font-medium mb-3 text-celadon-dark">Type d'assemblage</h4>
          
          <div className="space-y-2">
            <button
              onClick={() => setTypeAssemblage('haute-tenacite')}
              className={`w-full p-3 border-2 rounded-lg text-left transition-all ${
                typeAssemblage === 'haute-tenacite'
                  ? 'border-celadon bg-celadon text-white'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <p className="font-medium">Toile cousue fil haute ténacité</p>
              <p className="text-xs mt-1 opacity-90">Coutures renforcées pour une meilleure résistance</p>
            </button>
            
            <button
              onClick={() => setTypeAssemblage('tenara')}
              className={`w-full p-3 border-2 rounded-lg text-left transition-all ${
                typeAssemblage === 'tenara'
                  ? 'border-celadon bg-celadon text-white'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <p className="font-medium">Toile cousue fil Tenara™</p>
              <p className="text-xs mt-1 opacity-90">Fil PTFE haute résistance aux UV et intempéries</p>
            </button>
            
            <button
              onClick={() => setTypeAssemblage('soudee')}
              className={`w-full p-3 border-2 rounded-lg text-left transition-all ${
                typeAssemblage === 'soudee'
                  ? 'border-celadon bg-celadon text-white'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <p className="font-medium">Toile soudée</p>
              <p className="text-xs mt-1 opacity-90">Assemblage par soudure (sans fil visible)</p>
            </button>
          </div>
        </div>
      )}

      {/* Panneau dynamique pour Lestage du lambrequin */}
      {optionsSupp.oeillets && (
        <div className="mt-4 bg-celadon-light/20 border border-celadon-light rounded-lg p-4">
          <h4 className="text-md font-medium mb-3 text-celadon-dark">Lestage du lambrequin</h4>
          
          {/* Diamètre du tube */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Diamètre du tube (mm)</label>
            <div className="grid grid-cols-4 gap-2">
              {[10, 12, 14, 16].map((diametre) => (
                <button
                  key={diametre}
                  onClick={() => setLestageDetails(prev => ({ ...prev, diametreTube: diametre }))}
                  className={`p-2 border-2 rounded-lg text-center transition-all ${
                    lestageDetails.diametreTube === diametre
                      ? 'border-celadon bg-celadon text-white'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p className="text-sm font-medium">{diametre}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Nombre de lests (modifiable par le client) */}
          <div className="p-3 bg-white rounded border border-celadon-light">
            <label className="block text-sm text-gray-600 mb-2">Nombre de lests :</label>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setLestageDetails(prev => ({ ...prev, nombreLests: Math.max(0, prev.nombreLests - 1) }))}
                className="p-2 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
              >
                <Minus size={16} />
              </button>
              <input
                type="number"
                value={lestageDetails.nombreLests}
                onChange={(e) => setLestageDetails(prev => ({ ...prev, nombreLests: Math.max(0, parseInt(e.target.value) || 0) }))}
                className="w-20 px-3 py-2 border border-gray-300 rounded text-center font-bold text-lg"
                min="0"
              />
              <button
                onClick={() => setLestageDetails(prev => ({ ...prev, nombreLests: prev.nombreLests + 1 }))}
                className="p-2 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
              >
                <Plus size={16} />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Suggestion : {Math.ceil(largeur / 100)} lests (1 par mètre de largeur)
            </p>
          </div>
        </div>
      )}

      {/* Panneau dynamique pour Sanglons */}
      {optionsSupp.sanglons && isPergola && (
        <div className="mt-4 bg-celadon-light/20 border border-celadon-light rounded-lg p-4">
          <h4 className="text-md font-medium mb-3 text-celadon-dark">Configuration des sanglons</h4>
          
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Nombre de sanglons :</label>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setNombreSanglons(Math.max(0, nombreSanglons - 1))}
                className="p-2 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
              >
                <Minus size={16} />
              </button>
              <input
                type="number"
                value={nombreSanglons}
                onChange={(e) => setNombreSanglons(Math.max(0, Number(e.target.value)))}
                className="flex-1 px-4 py-2 border border-gray-300 rounded text-center font-medium max-w-[120px]"
                min="0"
              />
              <button
                onClick={() => setNombreSanglons(nombreSanglons + 1)}
                className="p-2 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
              >
                <Plus size={16} />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Sangles de fixation espacées tous les mètres
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default OptionsSupplementaires;
